/**
 * PER-SESSION provider override (F3-G, teacher/admin): "Configurar" button that
 * opens a dialog with the provider settings (schema-driven) and saves
 * via `PUT /api/live/sessions/:id/meeting`. Complements the cohort default.
 */
import { type Component, createSignal, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Settings2 } from "@fleet/icons";
import { useI18n } from "@/providers/i18n-provider";
import { Dialog } from "@/components/ui/dialog";
import { MeetingSettings } from "./meeting-settings";
import { liveApi, type LiveApi } from "../lib/live-api";

export interface SessionMeetingButtonProps {
  sessionId: string;
  initialProvider?: string | null;
  api?: LiveApi;
}

export const SessionMeetingButton: Component<SessionMeetingButtonProps> = (props) => {
  const { t } = useI18n();
  const [open, setOpen] = createSignal(false);
  const api = () => props.api ?? liveApi;
  const saveMeeting = async (provider: string, config: Record<string, unknown>) => {
    await api().setMeeting(props.sessionId, provider, config);
    setOpen(false);
  };
  return (
    <>
      <button
        type="button"
        class="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-muted-foreground hover:text-foreground"
        data-testid="session-meeting-open"
        aria-label={t(msg`Configure live provider`)}
        onClick={() => setOpen(true)}
      >
        <Settings2 class="h-3.5 w-3.5" /> {t(msg`Configure`)}
      </button>
      <Show when={open()}>
        <Dialog open={open()} onOpenChange={setOpen} contentClass="w-full max-w-md p-4">
          <MeetingSettings
            title={t(msg`Live provider for this session`)}
            initialProvider={props.initialProvider}
            onSave={saveMeeting}
          />
        </Dialog>
      </Show>
    </>
  );
};
